import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ 
  isCollapsed = false, 
  onToggle, 
  user, 
  className = "" 
}) => {
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { 
      path: '/member-dashboard', 
      label: 'Dashboard', 
      icon: 'Home',
      description: 'Your wellness overview'
    },
    { 
      path: '/progress-tracking', 
      label: 'Progress', 
      icon: 'TrendingUp',
      description: 'Track your journey'
    },
    { 
      path: '/community-forums', 
      label: 'Community', 
      icon: 'Users',
      description: 'Connect and learn',
      submenu: [
        { path: '/community-forums', label: 'Forums', icon: 'MessageSquare' },
        { path: '/events-and-workshops', label: 'Events', icon: 'Calendar' }
      ]
    },
    { 
      path: '/wellness-marketplace', 
      label: 'Marketplace', 
      icon: 'ShoppingBag',
      description: 'Discover wellness solutions'
    },
    { 
      path: '/member-profile', 
      label: 'Profile', 
      icon: 'User',
      description: 'Account settings'
    }
  ];

  const quickActions = [
    { label: 'Set Goal', icon: 'Target', action: 'goal' },
    { label: 'Join Event', icon: 'Calendar', action: 'event' },
    { label: 'New Post', icon: 'MessageSquare', action: 'post' },
    { label: 'Shop Wellness', icon: 'ShoppingBag', action: 'shop' }
  ];

  const isActive = (path) => {
    if (path === '/community-forums' && 
        (location?.pathname === '/community-forums' || location?.pathname === '/events-and-workshops')) {
      return true;
    }
    return location?.pathname === path;
  };

  const handleNavigation = (path) => {
    window.location.href = path;
  };

  const handleQuickAction = (action) => {
    setIsQuickActionsOpen(false);
    // Handle quick actions based on action type
    switch (action) {
      case 'goal': handleNavigation('/progress-tracking?action=new-goal');
        break;
      case 'event': handleNavigation('/events-and-workshops?action=register');
        break;
      case 'post': handleNavigation('/community-forums?action=new-post');
        break;
      case 'shop': handleNavigation('/wellness-marketplace');
        break;
      default:
        break;
    }
  };

  return (
    <>
      <aside className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-card border-r border-border z-40 transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      } ${className}`}>
        
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          {!isCollapsed && (
            <div className="flex items-center space-x-2">
              <Icon name="Compass" size={20} className="text-primary" />
              <span className="font-heading font-medium text-foreground">Navigation</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="h-8 w-8"
          >
            <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={16} />
          </Button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 p-2 space-y-1">
          {navigationItems?.map((item) => (
            <div key={item?.path}>
              <button
                onClick={() => handleNavigation(item?.path)}
                className={`flex items-center w-full px-3 py-3 rounded-lg text-sm font-medium transition-wellness group ${
                  isActive(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title={isCollapsed ? item?.label : ''}
              >
                <Icon name={item?.icon} size={18} className="flex-shrink-0" />
                {!isCollapsed && (
                  <div className="ml-3 flex-1 text-left">
                    <div className="font-medium">{item?.label}</div>
                    <div className="text-xs opacity-75 mt-0.5">{item?.description}</div>
                  </div>
                )}
              </button>

              {/* Community Submenu */}
              {item?.submenu && !isCollapsed && isActive(item?.path) && (
                <div className="ml-6 mt-1 space-y-1">
                  {item?.submenu?.map((subItem) => (
                    <button
                      key={subItem?.path}
                      onClick={() => handleNavigation(subItem?.path)}
                      className={`flex items-center w-full px-3 py-2 rounded-lg text-sm transition-wellness ${
                        location?.pathname === subItem?.path
                          ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={subItem?.icon} size={16} />
                      <span className="ml-2">{subItem?.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Quick Actions */}
        <div className="p-2 border-t border-border">
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setIsQuickActionsOpen(!isQuickActionsOpen)}
              className={`w-full ${isCollapsed ? 'px-0' : 'justify-start'}`}
              title={isCollapsed ? 'Quick Actions' : ''}
            >
              <Icon name="Zap" size={18} />
              {!isCollapsed && <span className="ml-2">Quick Actions</span>}
              {!isCollapsed && (
                <Icon 
                  name={isQuickActionsOpen ? "ChevronUp" : "ChevronDown"} 
                  size={16} 
                  className="ml-auto" 
                />
              )}
            </Button>

            {/* Quick Actions Menu */}
            {isQuickActionsOpen && (
              <div className={`absolute ${isCollapsed ? 'left-full ml-2' : 'top-full mt-2'} ${
                isCollapsed ? 'bottom-0' : 'left-0 right-0'
              } bg-popover border border-border rounded-lg shadow-wellness-lg z-50`}>
                <div className="p-2 space-y-1">
                  {quickActions?.map((action) => (
                    <button
                      key={action?.action}
                      onClick={() => handleQuickAction(action?.action)}
                      className="flex items-center w-full px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-wellness"
                    >
                      <Icon name={action?.icon} size={16} />
                      <span className="ml-2">{action?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* User Tier Status */}
        {!isCollapsed && user?.tier && (
          <div className="p-4 border-t border-border">
            <div className="flex items-center space-x-3 p-3 bg-accent/5 rounded-lg">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <Icon name="Award" size={18} color="white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {user?.name || 'Member'}
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="w-2 h-2 bg-accent rounded-full animate-breathing"></div>
                  <span className="text-xs font-caption text-accent-foreground">
                    {user?.tier} Member
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </aside>
      {/* Overlay for quick actions on mobile */}
      {isQuickActionsOpen && (
        <div 
          className="fixed inset-0 z-30"
          onClick={() => setIsQuickActionsOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;